/* Wizard · Step 4 · Treatment Plan
 * Extracted iter101.91 from `/app/frontend/src/pages/doctor/CaseSubmissionPage.jsx`.
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { confirmDialog, alertDialog, promptDialog } from "@/lib/confirm";
import {
    CaretRight, CaretLeft, CaretDown, CheckCircle, Circle, Upload, X, Check,
    MagnifyingGlass, ArrowsCounterClockwise, Crown, FileArrowUp, GitBranch,
    Warning, Lightning, Clock, Info, Tooth, Star, ShieldCheck, Cube, Cloud,
    CloudArrowUp, File as FileIcon, FilePdf, FileZip, FileVideo, Sparkle, Tag,
    ClipboardText, HeartStraight, Ruler, TrendUp, Sliders, Package, Prohibit,
    Notepad, ListBullets, Pencil, ArrowSquareUp, ArrowSquareDown, CircleHalf,
    Printer, BookmarkSimple, FloppyDisk, Trash, ChartBar, Hourglass, Calculator,
    Lock, UserGear, Camera, Stethoscope, HouseLine, Plus, Prescription,
    Headset, Truck, Receipt, SealCheck, Buildings, Hash, Scan, ArrowRight,
    User as UserIcon,
} from "@phosphor-icons/react";
import PerToothPlanAccordion from "@/components/case/PerToothPlanAccordion";
import DetailedClinicalExam, { ExamAccordion, present } from "@/components/case/DetailedClinicalExam";
import TreatmentObjectives from "@/components/case/TreatmentObjectives";
import CaseSubmittedDialog from "@/components/case/CaseSubmittedDialog";
import PlanTimelineSection, { RetentionRecallPair } from "@/components/case/PlanTimelineSection";
import AdvancePaymentBlock from "@/components/case/AdvancePaymentBlock";
import RequestScanVisitModal from "@/components/patient/RequestScanVisitModal";
import PatientAvatar from "@/components/patient/PatientAvatar";
import AddPatientDialog from "@/components/doctor/AddPatientDialog";
import DemoFillButton from "@/components/case-wizard/DemoFillButton";
import PrescriptionComplianceBlock from "@/components/case/PrescriptionComplianceBlock";
import WizardPaymentCommitment from "@/components/case/WizardPaymentCommitment";
import InvisiqueLogo from "@/components/site/Logo";
import {
    LabSection, LabChip, LabChoiceCard, LabInfoNote, LabGlanceRow,
    FdiChipPicker, PhaseSplitVisualizer,
} from "@/components/case/wizard/lab-section";
import {
    SelfPlanLabInstructions, StepLabInstructions, TierInclusionRow,
} from "@/components/case/wizard/steps/StepLabInstructions";
import {
    DraftSavedChip, ChildCaseBanner, StepIndicator, WizardStepHero,
    Step1SectionHeader, Step1ProgressSpine, Step1AnsweredStack,
    Step1AnsweredStrip, Step1ActiveWrap, Step1RecapBanner, RecapChip,
    ReviewBlock, Row, SelectField, SelectedPill,
    cardBadgeClass, resolveHeroIcon, fmtINR,
} from "@/components/case/wizard/shared";
import {
    ScanSourcePicker, RadioRailRow, CoeSupportCompliancePicker,
    PlanningModePicker, ModeCard,
} from "@/components/case/wizard/pickers";
import {
    IOScannerToggleCard, TierPicker, TierFeature, LabVisitScanFeeRow,
    PromoBlock, PricingAndOffersCard, PrescriptionPreviewCard,
    PreviewBlock, PreviewRow, CoeOrderPreviewCard, PriorityPicker,
    SlotUploader, PlanningSoftwareExportCard, CoeHandoffCallout,
} from "@/components/case/wizard/blocks";


export function StepTreatmentPlan({ rx, setRx, metadata, planningMode }) {
    const isCoeMode = planningMode === "coe";
    /* iter97.77 — Machine-readable plan export moved to Step 3
       (Scans & Photos) per user direction: all file uploads live in
       one step. This step now focuses purely on authoring intent —
       treatment objectives + the per-tooth tabbed accordion. */

    /* iter97.60 — PerToothPlanAccordion data adapter. The accordion expects
       `plan.{tooth_movements_plan,attachments_plan,precision_cuts,bite_ramps}`
       and an `onPlanChange(key, value)` callback. The doctor's `rx` state
       carries those four fields under the same names, so the adapter is a
       direct pass-through. */
    const onPerToothChange = (k, v) => setRx((p) => ({ ...p, [k]: v }));

    return (
        <div className="space-y-6" data-testid="case-wizard-treatment-plan">
            {/* iter97.55 — Treatment Objectives chip grid stays at the top.
                iter97.60 — The per-tooth tooth chart that used to live
                inside TreatmentObjectives is replaced by the tabbed
                PerToothPlanAccordion below, which also rolls in what was
                Step 5 (attachments, precision cuts, bite ramps). One step,
                one consolidated authoring surface. */}
            {isCoeMode ? (
                <>
                    <TreatmentObjectives
                        rx={rx}
                        setRx={setRx}
                        metadata={metadata}
                        showToothChart={false}
                    />
                    <div className="border-t border-border pt-5">
                        <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground mb-3">
                            Per-tooth plan
                        </div>
                        <CoeHandoffCallout subject="per-tooth plan (movements, attachments, precision cuts, bite ramps)" />
                    </div>
                </>
            ) : (
                <>
                    <TreatmentObjectives
                        rx={rx}
                        setRx={setRx}
                        metadata={metadata}
                        showToothChart={false}
                    />
                    {/* iter97.60/61 — Tabbed per-tooth authoring (movements,
                        attachments, precision cuts, bite ramps) — the same
                        component the planner uses. `headless` strips the
                        accordion shell so the wizard step IS the frame. */}
                    <PerToothPlanAccordion
                        plan={rx}
                        onPlanChange={onPerToothChange}
                        headless
                    />

                    {/* iter98.99q — 3rd-Party Deliverable was originally
                        added here (iter98.99p) but moved to Step 3
                        (Scans & Photos) per user direction. The natural
                        home for the planning-software + viewer-link
                        declaration is alongside the other plan-source
                        records (scans, photos, X-rays), not next to
                        the per-tooth authoring tabs. */}
                </>
            )}
        </div>
    );
}


/* iter98.99p — ThirdPartyDeliverableSection moved to its own module
   at /app/frontend/src/components/case/ThirdPartyDeliverableSection.jsx
   so it can be reused from ScansPhotosStep (iter98.99q — Self-Plan
   deliverable now lives at Step 3 instead of Step 4).  */


/* iter97.60 — StepAttachments removed. The per-tooth attachments,
   precision cuts, and bite ramps the doctor used to fill out on Step 5
   now live inside Step 4 (Treatment Plan) as a tabbed accordion. The
   legacy AttachmentsStep component is no longer mounted by the wizard
   but kept on disk for reference / potential reuse. */


/* ──────────────────────────────────────────────────────────────────
   iter97.75 — Self-plan Lab Instructions redesign
   ──────────────────────────────────────────────────────────────────
   Renders a section-based form (mirrors the Clinical Exam visual
   rhythm) for Self-Plan doctors. Each section is `LabSection` with a
   tinted Phosphor icon, title, optional sub-description, and a white
   inner card containing the form fields.

   Field bindings:
     • clinical.arch ← Arches in treatment
     • planTimeline.aligner_count_upper / _lower
     • planTimeline.wear_schedule_default_days
     • rx.ipr_directive  / attachments_directive / elastics_directive
     • rx.patient_flags  (["bruxism" | "tmj" | "none"])
     • rx.case_complexity ("mild" | "moderate" | "complex")
     • rx.shipment_preference ("all_at_once" | "phased")
     • rx.shipment_notes  (free text, shown only when phased)
     • rx.do_not_touch_teeth
     • rx.lab_notes
*/

/* iter98.99r — `LabSection` now defaults to accordion behaviour.
   Each section's header is a clickable row that toggles the body.
   Pass `defaultOpen` to seed the initial state, or `open`+`onToggle`
   for controlled use. Doctors get a quiet, scannable Step 5 by
   default (first section open, rest collapsed) instead of one mega
   scroll. */
